import { useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { ReactCompareSlider, ReactCompareSliderImage } from 'react-compare-slider';
import { GlobalNavbar } from '../components/global-navbar';
import { OptInSection } from '../components/OptInSection';
import { useSeo } from '../utils/useSeo';

const featureBullets = [
  {
    benefit: 'Phone photos to studio results.',
    detail:
      "You do not need a DSLR or a photo studio. Take the shot on your phone and let the AI handle the rest. Background cleanup, brightness, clarity, and contrast are all processed automatically.",
  },
  {
    benefit: 'Clean, consistent look across every listing.',
    detail:
      "Every car you list looks like it belongs to the same professional dealer. Consistent visual quality across your inventory builds buyer trust before they have even read your description.",
  },
  {
    benefit: 'Choose from 1-3 studio styles.',
    detail:
      "Select the backdrop and finish that matches your brand or the type of car you are selling, from clean white studio to outdoor showroom looks.",
  },
  {
    benefit: 'Better photos mean stronger asking prices.',
    detail:
      'When a listing looks professional, buyers arrive with a different frame of mind. They see a dealer who takes quality seriously and that perception holds prices up through negotiation.',
  },
  {
    benefit: 'No editing skills required.',
    detail:
      'You are not a photographer. You do not need to be. AI Photography is designed for dealers who need to get cars listed fast, not spend 30 minutes per photo in Lightroom.',
  },
] as const;

const howItWorksSteps = [
  {
    description:
      'Use your phone on the lot. Standard angles like front, rear, and side. No special setup needed. No lighting equipment. Just shoot.',
    title: 'Step 1 - Take the photo',
  },
  {
    description: 'Drop the photo into the AI Photography tool inside your Profit Cars dashboard.',
    title: 'Step 2 - Upload to Profit Cars',
  },
  {
    description:
      'Select from up to 3 studio-quality styles, clean background finishes designed to make the car the only thing the buyer sees.',
    title: 'Step 3 - Choose your style',
  },
  {
    description:
      'Your professional image is ready in seconds. Use it directly on Mobile.de, AutoScout24, or any other listing platform you sell on.',
    title: 'Step 4 - Download and list',
  },
] as const;

const faqs = [
  {
    answer:
      'No. AI Photography is designed to work with standard smartphone photos. Take the shot on the lot with whatever phone you are using and the AI handles the enhancement from there.',
    question: 'Do I need a professional camera or special equipment?',
  },
  {
    answer:
      'The tool processes background removal and replacement, brightness and contrast correction, clarity enhancement, and color balancing. The result is a clean, studio-style image ready to publish directly to listing platforms.',
    question: 'What does the AI actually change in the photo?',
  },
  {
    answer: 'Profit Cars AI Photography currently offers 1-3 studio styles so you can choose the look that best fits your inventory or brand.',
    question: 'How many styles are available?',
  },
  {
    answer:
      'Yes. The output images are optimized for online listing platforms across the EU, including the major marketplaces EU dealers use daily.',
    question: 'Will the photos work on Mobile.de and AutoScout24?',
  },
  {
    answer: 'None at all. The process is upload, choose a style, download. No software knowledge required.',
    question: 'Do I need editing skills to use it?',
  },
  {
    answer:
      'Buyers form price expectations before they contact you. Professional-looking photos signal a cared-for car and a credible seller, both of which give you more room to hold your asking price in negotiation. Listings with amateur photos invite buyers to open negotiations lower.',
    question: 'Why does photo quality affect the price I get?',
  },
] as const;

export function AiPhotographyPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const handleScrollToOptIn = () => {
    document.getElementById('optin-get')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  useSeo({
    description:
      "Turn phone photos into professional studio-quality car images in one click. Profit Cars' AI Photography gives every listing a consistent, clean look - no photographer, no editing skills, no wasted time.",
    image: '/after.jpeg',
    keywords: [
      'ai photography for car dealers',
      'car listing photo enhancer',
      'mobile.de listing photos',
      'autoscout24 listing photos',
      'studio quality car photos',
    ],
    path: '/features/ai-photography',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      description:
        "AI Photography transforms smartphone car photos into studio-quality listing images for EU dealers selling on platforms like Mobile.de and AutoScout24.",
      name: 'AI Photography - Studio-Quality Car Listing Photos',
      url: '/features/ai-photography',
    },
    title: 'AI Photography - Studio-Quality Car Listing Photos From Your Phone | Profit Cars',
    type: 'website',
  });

  return (
    <main className="min-h-screen bg-[#ececec] text-[#111827]">
      <GlobalNavbar />

      <section className="border-b border-black/10 bg-[#e6e7ea]">
        <div className="mx-auto grid w-full max-w-6xl gap-8 px-6 pb-12 pt-14 lg:grid-cols-[1fr_1fr] lg:items-center lg:px-8">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#2f5b1b]">AI Photography</p>
            <h1 className="mt-4 max-w-4xl font-display text-5xl leading-[0.94] text-black sm:text-6xl">
              Every Listing Looks Like It Was Shot in a Studio.
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-700 sm:text-lg">
              AI Photography transforms the photos you take on your phone into clean, bright, studio-quality images in
              one click. Because buyers judge the car before they read a single word.
            </p>

            <div className="mt-8">
              <button
                className="inline-flex h-12 items-center justify-center rounded-[10px] border border-black/65 bg-[#08090c] px-7 text-sm font-bold text-white transition hover:border-white/35 hover:bg-[#141820]"
                onClick={handleScrollToOptIn}
                type="button"
              >
                Get Access to Profit Cars
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="rounded-[24px] border border-black/10 bg-[#f3f4f6] p-4 sm:p-5">
            <div className="relative aspect-[75/56] overflow-hidden rounded-[18px] border border-black/15 bg-black">
              <ReactCompareSlider
                className="h-full w-full"
                defaultPosition={56}
                onlyHandleDraggable={false}
                transition="0.24s cubic-bezier(0.22,1,0.36,1)"
                itemOne={
                  <ReactCompareSliderImage
                    alt="Before listing visual"
                    src="/before-optimized.jpeg"
                    style={{
                      filter: 'brightness(0.78) saturate(0.65)',
                      height: '100%',
                      objectFit: 'cover',
                      width: '100%',
                    }}
                  />
                }
                itemTwo={
                  <ReactCompareSliderImage
                    alt="After listing visual"
                    src="/after.jpeg"
                    style={{ height: '100%', objectFit: 'cover', width: '100%' }}
                  />
                }
              />

              <div className="pointer-events-none absolute left-4 top-4 z-20 whitespace-nowrap rounded-full bg-black/72 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-white">
                Before
              </div>
              <div className="pointer-events-none absolute right-4 top-4 z-20 whitespace-nowrap rounded-full bg-[#53b12c]/90 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-black">
                After
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-16">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <h2 className="max-w-5xl text-3xl font-extrabold leading-tight text-black sm:text-4xl">
            Bad photos are quietly killing your asking price.
          </h2>
          <div className="mt-6 max-w-5xl space-y-5 text-base leading-relaxed text-slate-700 sm:text-lg">
            <p>
              You know the car. You know what it is worth. But the buyer browsing Mobile.de or AutoScout24 does not.
              They only know what they see.
            </p>
            <p>
              Dark lot photos. Cluttered backgrounds. A grey sky reflected in the bonnet. These are not just aesthetic
              problems. They are margin problems.
            </p>
            <p>
              Buyers use photo quality to make fast judgments about the car condition and the seller credibility. A
              listing that looks amateur invites low offers and hard negotiation. A listing that looks clean and
              professional means buyers come in with a different mindset and are less likely to haggle the price down
              before they have even seen the car in person.
            </p>
            <p>
              Most independent dealers do not have the budget or time for a professional photographer on every car. So
              the photos suffer and so does the margin.
            </p>
          </div>
        </div>
      </section>

      <section className="border-y border-black/10 bg-[#f1f2f4] py-14 sm:py-16">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <h2 className="max-w-4xl text-3xl font-extrabold leading-tight text-black sm:text-4xl">
            One click. Studio-quality photos. Every listing.
          </h2>
          <p className="mt-5 max-w-5xl text-base leading-relaxed text-slate-700 sm:text-lg">
            AI Photography is built into Profit Cars so you can go from phone shot to listing-ready image in seconds.
            Take the photo on the lot, upload it, choose your style, and get a clean, professionally edited image back
            instantly. No editing software. No photographer. No extra steps.
          </p>

          <ul className="mt-8 grid gap-4 md:grid-cols-2">
            {featureBullets.map((item) => (
              <li className="rounded-[16px] border border-black/10 bg-white p-5" key={item.benefit}>
                <p className="text-base font-extrabold text-black">{item.benefit}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-700 sm:text-base">{item.detail}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-14 sm:py-16">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold leading-tight text-black sm:text-4xl">
            From lot to listing in under a minute.
          </h2>
          <div className="mt-8 grid gap-4 md:grid-cols-2">
            {howItWorksSteps.map((step) => (
              <article className="rounded-[16px] border border-black/10 bg-white p-5" key={step.title}>
                <h3 className="text-lg font-extrabold leading-tight text-black">{step.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-700 sm:text-base">{step.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-black/10 bg-[#f1f2f4] py-14 sm:py-16">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <h2 className="max-w-5xl text-3xl font-extrabold leading-tight text-black sm:text-4xl">
            Why Car Listing Photos Directly Affect What Price You Get
          </h2>
          <div className="mt-6 max-w-5xl space-y-6 text-base leading-relaxed text-slate-700 sm:text-lg">
            <p>
              The used car market is visual. Before a buyer reads your mileage, your service history, or your asking
              price, they have already formed an opinion based on your photos. That opinion affects whether they click
              through, whether they call, and how aggressively they negotiate when they do.
            </p>
            <p>
              Research consistently backs this up. According to data cited by industry sources, upgrading from
              low-quality to high-quality listing photos can increase engagement by over 170 percent, and
              professional-quality images have been linked to cars selling faster and for stronger prices. Buyers who
              see sharp, clean, well-lit images enter the conversation with a higher perceived value for the vehicle,
              making it easier to defend your asking price.
            </p>

            <div>
              <h3 className="text-xl font-extrabold text-black sm:text-2xl">
                The problem for independent EU dealers
              </h3>
              <p className="mt-2">
                Large dealership groups often have in-house photographers, branded photo booths, or dedicated
                merchandising staff. Independent dealers and car flippers do not. The result is a visual quality gap
                that consistently works against the smaller operator, not because their cars are worse, but because
                their photos look worse.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-extrabold text-black sm:text-2xl">What AI photography tools change</h3>
              <p className="mt-2">
                AI-powered car photo tools level that gap. They automate the work that used to require either
                expensive equipment or skilled editing: background removal, lighting correction, color balancing, and
                consistent presentation. What used to take 20-30 minutes per car in manual editing now takes seconds,
                and the output quality is comparable to purpose-built photo studios.
              </p>
              <p className="mt-2">
                For EU dealers listing on Mobile.de, AutoScout24, or similar platforms, listing photo quality is one
                of the highest-leverage, lowest-effort improvements available. Most competitors are still using raw lot
                photos. Listing consistently with cleaner, brighter, professional-looking images immediately signals a
                different level of professionalism to buyers before any other factor comes into play.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 sm:py-16">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold leading-tight text-black sm:text-4xl">
            Dealers who upgraded their photos changed what buyers offer them.
          </h2>
          <div className="mt-8 grid gap-4 lg:grid-cols-2">
            <blockquote className="rounded-[16px] border border-black/10 bg-white p-6">
              <p className="text-base leading-relaxed text-slate-800">
                "The photo tool makes every listing look professional. Cleaner photos mean better first impressions and
                we can hold stronger prices when buyers call."
              </p>
              <footer className="mt-4 text-sm font-semibold text-slate-600">
                David R., Independent Dealer - Barcelona
              </footer>
            </blockquote>
            <blockquote className="rounded-[16px] border border-black/10 bg-white p-6">
              <p className="text-base leading-relaxed text-slate-800">
                "Our team now follows one clear process from sourcing to sale. Everything is tracked and we make
                decisions faster because the margins are clear."
              </p>
              <footer className="mt-4 text-sm font-semibold text-slate-600">
                Andreas V., Dealer Group Buyer - Vienna
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      <section className="border-y border-black/10 bg-[#f1f2f4] py-14 sm:py-16">
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold leading-tight text-black sm:text-4xl">Questions about AI Photography</h2>
          <div className="mt-8 space-y-3">
            {faqs.map((faq) => (
              <article className="rounded-[14px] border border-black/10 bg-white p-5" key={faq.question}>
                <h3 className="text-lg font-extrabold leading-tight text-black">{faq.question}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-700 sm:text-base">{faq.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <OptInSection className="py-16 sm:py-20" id="optin-get" source="final_cta" />
    </main>
  );
}
